import axios from 'axios';
import Layout from '../shared/Layout';
import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import language from '../../variables/language';
import ScheduleContainer from './ScheduleContainer';
import ScheduleLessonCreateModal from './ScheduleLessonCreateModal';
import LessonDetailModal from '../students/LessonDetailModal';
import IconAdd from '../../components/icons/IconAdd';
import IconFilter from '../../components/icons/IconFilter';
import IconCycled from '../../components/icons/IconCycled';
import IconGoogleCalendar from '../../components/icons/IconGoogleCalendar';
import IconCoins from '../../components/icons/IconCoins';
import IconClock from '../../components/icons/IconClock';
import DateInput from '../../components/forms/DateInput';
import '../../css/student-card.css';
import '../../css/schedule.css';

const LUXON_LOCALE_BY_LANG = { en: 'en', ua: 'uk', pl: 'pl' };

// The 4 ways /api/schedule can be asked for a week are mutually exclusive
// server-side (ScheduleController checks dateFrom+dateTo, then bias, then
// cycled, then falls back to the current week - see the Java controller),
// so this page only ever has exactly one of these "modes" active at a
// time. Previously that was implicit - 3 separate useEffects independently
// overwriting one shared query-string state, whichever fired last winning.
// Explicit `activeMode` makes the exclusivity a first-class UI concept
// instead of an accident of effect ordering.
const Schedule = () => {
   const lang = language[language.lang];
   const locale = LUXON_LOCALE_BY_LANG[language.lang] || 'en';

   const [activeMode, setActiveMode] = useState('week'); // 'week' | 'range' | 'cycled'
   const [weekBias, setWeekBias] = useState(0);
   const [dateFrom, setDateFrom] = useState('');
   const [dateTo, setDateTo] = useState('');
   const [filterOpen, setFilterOpen] = useState(false);

   const [refreshContainer, setRefreshContainer] = useState(0);
   const bumpRefresh = () => setRefreshContainer(prev => prev + 1);

   const [createOpen, setCreateOpen] = useState(false);
   const [modalState, setModalState] = useState('none');
   const [toUpdateId, setToUpdateId] = useState('');

   const [stats, setStats] = useState([]);

   const [calendarSync, setCalendarSync] = useState('idle'); // 'idle' | 'running' | 'done' | 'failed'
   const runCalendarSync = () => {
      if (calendarSync === 'running') return;
      setCalendarSync('running');
      axios.post('/api/schedule/trigger/sync-google-calendar', {}, { withCredentials: true })
         .then(() => { setCalendarSync('done'); bumpRefresh(); })
         .catch(() => setCalendarSync('failed'));
   };

   const stateHundler = (state, id) => {
      if (state === 'menu') { setModalState('menu'); setToUpdateId(id); }
      else if (state === 'none') { setModalState('none'); }
   };

   // a date range fully picked auto-switches into range mode; clearing
   // either side falls back to whatever week was showing before - no
   // separate "apply" button needed, same instant-apply feel as every
   // other DateInput-based filter in this app.
   useEffect(() => {
      if (dateFrom && dateTo) setActiveMode('range');
      else if (activeMode === 'range') setActiveMode('week');
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [dateFrom, dateTo]);

   const query = (() => {
      if (activeMode === 'range' && dateFrom && dateTo) return 'dateFrom=' + dateFrom + '&dateTo=' + dateTo;
      if (activeMode === 'cycled') return 'cycled=1';
      return 'bias=' + weekBias;
   })();
   const urlSchedule = '/api/schedule?' + query;
   const urlInfopanel = '/api/schedule/infopanel?' + query;

   useEffect(() => {
      axios.get(urlInfopanel, { withCredentials: true })
         .then(response => setStats(Array.isArray(response.data) ? response.data : []))
         .catch(() => setStats([]));
   }, [urlInfopanel, refreshContainer]);

   const goPrevWeek = () => { setActiveMode('week'); setWeekBias(b => b - 1); };
   const goNextWeek = () => { setActiveMode('week'); setWeekBias(b => b + 1); };
   const goToday = () => { setActiveMode('week'); setWeekBias(0); };
   const toggleCycled = () => {
      const turningOn = activeMode !== 'cycled';
      setActiveMode(turningOn ? 'cycled' : 'week');
      if (turningOn) { setFilterOpen(false); setDateFrom(''); setDateTo(''); }
   };
   const clearRange = () => { setDateFrom(''); setDateTo(''); setActiveMode('week'); };

   const weekStart = DateTime.now().setLocale(locale).plus({ weeks: weekBias }).startOf('week');
   const weekEnd = weekStart.plus({ days: 6 });
   const weekRangeLabel = weekStart.toFormat('dd.MM') + ' – ' + weekEnd.toFormat('dd.MM.yyyy');

   const statValue = (id) => {
      const found = stats.find(s => s.id === id);
      return found ? found.value : null;
   };
   const sumPrice = statValue(1);
   const sumHours = statValue(2);

   return (
      <Layout>
         <main className='main-block'>
            <div className='main-content txt-c schedule-page'>

               <div className='schedule-header'>
                  <span className='schedule-header-title'>{lang.schedule}</span>
                  <div className='schedule-stats'>
                     {sumPrice !== null ? (
                        <span className='student-lessons-stat-pill'><b><IconCoins style={{ verticalAlign: -2, marginRight: 3 }} />{sumPrice}</b><small>{lang['Sum price']}</small></span>
                     ) : ''}
                     {sumHours !== null ? (
                        <span className='student-lessons-stat-pill'><b><IconClock style={{ verticalAlign: -2, marginRight: 3, width: 13, height: 13 }} />{sumHours}</b><small>{lang['Sum hours']}</small></span>
                     ) : ''}
                     <button type='button' className='schedule-sync-btn' onClick={runCalendarSync} disabled={calendarSync === 'running'}
                        title={lang.syncCalendar}>
                        <IconCycled style={{ width: 14, height: 14 }} />
                        <IconGoogleCalendar style={{ width: 14, height: 14 }} />
                        {calendarSync === 'running' ? lang.syncCalendarRunning : lang.syncCalendar}
                     </button>
                     {calendarSync === 'done' ? <span className='schedule-sync-status is-done'>{lang.syncCalendarDone}</span> : ''}
                     {calendarSync === 'failed' ? <span className='schedule-sync-status is-failed'>{lang.syncCalendarFailed}</span> : ''}
                  </div>
               </div>

               <div className='schedule-toolbar'>
                  <div className='schedule-week-nav'>
                     <span className='schedule-week-nav-btn' onClick={goPrevWeek} title={lang.dateFrom}>
                        <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'><polyline points='15 18 9 12 15 6' /></svg>
                     </span>
                     <span className={'schedule-week-today-btn' + (activeMode === 'week' && weekBias === 0 ? ' active' : '')} onClick={goToday}>{lang.today}</span>
                     <span className='schedule-week-nav-btn' onClick={goNextWeek} title={lang.dateTo}>
                        <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'><polyline points='9 18 15 12 9 6' /></svg>
                     </span>
                  </div>
                  {activeMode === 'week' ? <span className='schedule-week-range'>{weekRangeLabel}</span> : ''}

                  <span className={'schedule-filter-toggle' + (activeMode === 'range' ? ' active' : '') + (filterOpen ? ' open' : '')}
                     onClick={() => setFilterOpen(prev => !prev)}>
                     <IconFilter />{lang.dateRangeFilter}
                  </span>

                  <span className={'schedule-cycled-toggle' + (activeMode === 'cycled' ? ' active' : '')} onClick={toggleCycled}>
                     <IconCycled fillColor={activeMode === 'cycled' ? '' : 'icon-red'} />{lang.cycledLessonsFilter}
                  </span>

                  <span className='schedule-spacer' />

                  <span className='schedule-add-btn' onClick={() => setCreateOpen(true)}>
                     <IconAdd />{lang.groupCard.addLessonButton}
                  </span>

                  <div className={'schedule-filter-row' + (filterOpen ? ' open' : '')}>
                     <DateInput value={dateFrom} onChange={setDateFrom} placeholder={lang.dateFrom} />
                     <DateInput value={dateTo} onChange={setDateTo} placeholder={lang.dateTo} />
                     {dateFrom || dateTo ? <span className='schedule-filter-clear' onClick={clearRange}>×</span> : ''}
                  </div>
               </div>

               <div className='schedule-body'>
                  <ScheduleContainer url={urlSchedule} refresh={refreshContainer} onOpenLesson={(id) => stateHundler('menu', id)} />
               </div>

               <LessonDetailModal isOpen={modalState === 'menu'} lessonId={toUpdateId}
                  onClose={() => stateHundler('none')} onSaved={bumpRefresh}
                  onParticipantsChanged={bumpRefresh} />

               <ScheduleLessonCreateModal isOpen={createOpen} cycled={activeMode === 'cycled'}
                  onClose={() => setCreateOpen(false)}
                  onCreated={() => { setCreateOpen(false); bumpRefresh(); }} />

            </div>
         </main>
      </Layout>
   )
}

export default Schedule
